import { useState } from 'react'
import { Outlet, useOutletContext, useParams } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import {
  AtsJobApplicationFilesPageDocument,
  AtsJobApplicationFilesPageFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'

import { ApplicationFiles } from './components/ApplicationFiles'
import { DiscTest } from './components/DiscTest'
import { OfferLetter } from './components/OfferLetter'

interface OutletContext {
  application: AtsJobApplicationFilesPageFragment
  isReviewerRole: boolean
}

const JobApplicationFilesPage = () => {
  const [activeTab, setActiveTab] = useState<'general-files' | 'offer-letter' | undefined>(
    'general-files'
  )
  const { company } = useCurrentSession()
  const { application, isReviewerRole } = useOutletContext<OutletContext>()
  const params = useParams()
  const id = String(params.application_id)
  const jobPostingId = String(params.jobId)

  const { data } = useQuery(AtsJobApplicationFilesPageDocument, {
    variables: { companyId: company.id },
  })

  const { data: companyInstallationsData } = useGetMarketplaceCompanyInstallations(
    {
      companyIds: [company.id],
    },
    false
  )

  const offerLetterEnabled =
    data?.features.enabledFeatures.nodes.find((node) => node.name === 'ats_offer_letter')
      ?.enabled ?? false

  const isIdealDiscFeatureEnabled =
    data?.features.enabledFeatures.nodes.find((n) => n.name === 'ats_ideal_disc_integration')
      ?.enabled ?? false

  const isIdealDiscInstalled = companyInstallationsData.some(
    (installation) => installation.integrationDatoId === integrationCodes.idealdisc
  )

  const isIdealDiscEnabled = isIdealDiscFeatureEnabled || isIdealDiscInstalled

  if (!application || isReviewerRole) {
    return null
  }

  const currentTab = activeTab ?? 'general-files'
  const tabs = [
    {
      label: i18n.t('ats.candidates.files.tabs.general_files'),
      id: 'general-files',
      index: true,
      onClick: () => setActiveTab('general-files'),
    },
  ]

  if (offerLetterEnabled) {
    tabs.push({
      label: i18n.t('ats.candidates.files.tabs.offer_letter'),
      id: 'offer-letter',
      index: false,
      onClick: () => setActiveTab('offer-letter'),
    })
  }

  return (
    <>
      {tabs.length > 1 && <Tabs secondary tabs={tabs} />}
      <Box gap="s24" padding="s24">
        {currentTab === 'general-files' && (
          <>
            {isIdealDiscEnabled && application.assessment && (
              <DiscTest assessment={application.assessment} />
            )}
            <ApplicationFiles application={application} />
          </>
        )}
        {currentTab === 'offer-letter' && (
          <OfferLetter applicationId={id} jobPostingId={jobPostingId} />
        )}
        <Outlet context={{ application }} />
      </Box>
    </>
  )
}

export default JobApplicationFilesPage
